/**
 * Aviso de contratos por vencer, al entrar en la aplicación.
 *
 * Al abrir la aplicación se revisan los pendientes (la misma consulta que la campana, así que no
 * cuesta una petición más) y, si hay contratos a los que les quedan menos de tres meses —o el
 * preaviso pactado, si es más largo— sin renovación ni rescisión subida, salta esta tarjeta abajo
 * a la derecha con la lista. Es la alerta `vencimiento_contrato` del motor de alertas
 * (`apps/api/src/services/alerts.ts`).
 *
 * Una vez por sesión: si se cierra con «Entendido», no vuelve hasta la próxima vez que se entre…
 * salvo que aparezca un contrato NUEVO en la lista, que eso sí merece volver a avisar.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import type { PendingTask } from '@naar/shared';
import { api, queryKeys } from '../api/client';
import { formatShortDate } from '../lib/format';
import { Icon } from './Icon';
import styles from './ContractExpiryNotice.module.css';

const CLAVE = 'naar:aviso-contratos:visto';
/** Un respiro tras cargar: que no salte encima de la pantalla mientras aún se está pintando. */
const RETRASO_MS = 1200;
const EN_LA_TARJETA = 4;

const esContrato = (task: PendingTask) => task.category === 'contrato' && task.id.startsWith('alerta:');

function diasHasta(iso: string | null): number | null {
  if (!iso) return null;
  const hoy = new Date();
  const inicio = Date.UTC(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  const [anio, mes, dia] = iso.split('-').map(Number) as [number, number, number];
  return Math.round((Date.UTC(anio, mes - 1, dia) - inicio) / 86_400_000);
}

type Nivel = 'vencido' | 'urgente' | 'pronto' | 'tiempo';

/** Cuánto aprieta: vencido, 15 días o menos, un mes o menos, o todavía hay margen. */
function nivelDe(dias: number | null): Nivel {
  if (dias === null) return 'tiempo';
  if (dias < 0) return 'vencido';
  if (dias <= 15) return 'urgente';
  if (dias <= 31) return 'pronto';
  return 'tiempo';
}

/** La etiqueta de la derecha: corta, para leerla de un vistazo. */
function etiqueta(dias: number | null): string {
  if (dias === null) return 'Sin fecha';
  if (dias < 0) return 'Vencido';
  if (dias === 0) return 'Hoy';
  if (dias === 1) return 'Mañana';
  return `${dias} días`;
}

/** La frase de debajo del nombre, con la fecha. */
function detalle(dias: number | null, fecha: string | null): string {
  if (dias === null || !fecha) return '';
  if (dias < 0) return `venció hace ${-dias} día${dias === -1 ? '' : 's'}`;
  if (dias === 0) return 'vence hoy';
  return `vence el ${formatShortDate(fecha)}`;
}

/** «de», «la»… no cuentan: Lorenzo de Paola es LP, no LD. */
const PARTICULAS = new Set(['de', 'del', 'la', 'las', 'los', 'el', 'da', 'do', 'dos', 'van', 'von']);

function iniciales(nombre: string): string {
  // Con dos titulares («A y B»), las del primero.
  const partes = nombre
    .replace(/\s+y\s+.*/i, '')
    .trim()
    .split(/\s+/)
    .filter((parte) => !PARTICULAS.has(parte.toLowerCase()));
  return ((partes[0]?.[0] ?? '') + (partes[1]?.[0] ?? '')).toUpperCase() || '·';
}

function leerVistos(): string {
  try {
    return sessionStorage.getItem(CLAVE) ?? '';
  } catch {
    return '';
  }
}

export function ContractExpiryNotice() {
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: queryKeys.pending(),
    queryFn: () => api.pending(),
    staleTime: 60_000,
  });
  const [listo, setListo] = useState(false);
  const [cerrado, setCerrado] = useState(false);

  useEffect(() => {
    const temporizador = window.setTimeout(() => setListo(true), RETRASO_MS);
    return () => window.clearTimeout(temporizador);
  }, []);

  const contratos = useMemo(
    () =>
      (data?.tasks ?? [])
        .filter(esContrato)
        .map((task) => ({ task, dias: diasHasta(task.dueDate) }))
        .sort((a, b) => (a.dias ?? 9999) - (b.dias ?? 9999)),
    [data],
  );
  // La «firma» de lo avisado: si cambia (entra un contrato nuevo), se vuelve a avisar.
  const firma = contratos.map(({ task }) => task.id).sort().join('|');
  const yaVisto = firma !== '' && leerVistos() === firma;

  if (!listo || cerrado || contratos.length === 0 || yaVisto) return null;

  function cerrar() {
    try {
      sessionStorage.setItem(CLAVE, firma);
    } catch {
      // Sin almacenamiento: se cierra igual, solo que en esta pestaña.
    }
    setCerrado(true);
  }

  const vencidos = contratos.filter(({ dias }) => dias !== null && dias < 0).length;
  const urgentes = contratos.filter(({ dias }) => dias !== null && dias >= 0 && dias <= 15).length;
  const masAdelante = contratos.length - vencidos - urgentes;
  const grave = vencidos + urgentes > 0;
  const visibles = contratos.slice(0, EN_LA_TARJETA);
  const resto = contratos.length - visibles.length;

  function irAPendientes() {
    cerrar();
    navigate('/seguimiento?tab=pendientes');
  }

  return (
    <aside
      className={grave ? `${styles.aviso} ${styles.avisoGrave}` : styles.aviso}
      role="alert"
      aria-labelledby="aviso-contratos-titulo"
    >
      <div className={styles.cabecera}>
        <span className={styles.icono} aria-hidden="true">
          <span className={styles.iconoOnda} />
          <Icon name="bell" size={19} strokeWidth={2} />
        </span>
        <div className={styles.titulos}>
          <span className={styles.antetitulo}>Contratos</span>
          <strong id="aviso-contratos-titulo" className={styles.titulo}>
            {contratos.length === 1 ? 'Un contrato vence pronto' : `${contratos.length} contratos vencen pronto`}
          </strong>
        </div>
        <button type="button" className={styles.cerrar} onClick={cerrar} aria-label="Cerrar el aviso" title="Cerrar">
          <Icon name="close" size={16} strokeWidth={2} />
        </button>
      </div>

      <p className={styles.subtitulo}>Les quedan menos de 3 meses y no tienen renovación ni rescisión subida.</p>

      {/* El resumen en tres cifras: lo que ya ha pasado, lo de estos días y lo de más adelante. */}
      <div className={styles.resumen}>
        {vencidos > 0 && (
          <span className={`${styles.chip} ${styles.chipVencido}`}>
            <b>{vencidos}</b> {vencidos === 1 ? 'vencido' : 'vencidos'}
          </span>
        )}
        {urgentes > 0 && (
          <span className={`${styles.chip} ${styles.chipUrgente}`}>
            <b>{urgentes}</b> en 15 días
          </span>
        )}
        {masAdelante > 0 && (
          <span className={styles.chip}>
            <b>{masAdelante}</b> más adelante
          </span>
        )}
      </div>

      <ul className={styles.lista}>
        {visibles.map(({ task, dias }) => {
          const nivel = nivelDe(dias);
          const nombre = task.tenant?.fullName ?? task.title;
          return (
            <li key={task.id}>
              <Link to={task.link} className={`${styles.fila} ${styles[`fila_${nivel}`]}`} onClick={cerrar}>
                <span className={styles.avatar} aria-hidden="true">
                  {iniciales(nombre)}
                </span>
                <span className={styles.filaTexto}>
                  <span className={styles.nombre}>{nombre}</span>
                  <span className={styles.meta}>
                    {[task.property?.name, detalle(dias, task.dueDate)].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <span className={`${styles.pastilla} ${styles[`pastilla_${nivel}`]}`}>{etiqueta(dias)}</span>
              </Link>
            </li>
          );
        })}
      </ul>
      {resto > 0 && (
        <button type="button" className={styles.resto} onClick={irAPendientes}>
          Y {resto} más
          <Icon name="arrow-right" size={13} strokeWidth={2.2} />
        </button>
      )}

      <div className={styles.acciones}>
        <button type="button" className={styles.botonSecundario} onClick={cerrar}>
          Entendido
        </button>
        <button type="button" className={styles.botonPrincipal} onClick={irAPendientes}>
          Ver en Pendientes
          <Icon name="arrow-right" size={15} strokeWidth={2.2} />
        </button>
      </div>
    </aside>
  );
}
